/**
 * Assessment Report Page - Public Repository Assessment Display
 *
 * PUBLIC PAGE - No authentication required.
 * Displays the full assessment report for a repository scanned by Equilateral AI.
 * Linked from GitHub Issues created during initial repository scan.
 *
 * Route: /assessment/:repoName (URL-encoded owner/repo)
 * Example: /assessment/JamesFord-HappyHippo%2Fmame-browser
 *
 * Features:
 * - Full analysis results (languages, frameworks, structure)
 * - Insights and recommendations
 * - Concerns and improvement opportunities
 * - Link to GitHub Issue
 * - CTA to sign up for full platform access
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Button,
  Progress,
  Table,
} from 'flowbite-react';
import {
  HiCheckCircle,
  HiXCircle,
  HiLightBulb,
  HiExclamation,
  HiExternalLink,
  HiCode,
  HiFolder,
  HiDocument,
  HiArrowRight,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

interface AssessmentAnalysis {
  Languages: Record<string, string>;
  Frameworks: string[];
  File_Count: number;
  Has_Tests: boolean;
  Has_CI: boolean;
  Has_Docker: boolean;
  Has_Infrastructure: boolean;
  Insights: string[];
  Concerns: string[];
  Directory_Structure: string[];
  Key_Files: Array<{ file: string; path: string; category: string }>;
  Dependency_Count?: number;
  Dev_Dependency_Count?: number;
}

interface Assessment {
  Assessment_ID: string;
  Repository: string;
  Status: string;
  Created_At: string;
  Completed_At: string;
  Analysis: AssessmentAnalysis;
  GitHub_Issue: {
    Number: number;
    URL: string;
  } | null;
  Context: {
    Project_Name: string | null;
  };
}

// =============================================================================
// API CONFIG
// =============================================================================

const API_BASE = process.env.REACT_APP_API_URL_CORE || process.env.REACT_APP_API_URL || '';

// =============================================================================
// COMPONENT
// =============================================================================

const AssessmentReportPage: React.FC = () => {
  const { repoName } = useParams<{ repoName: string }>();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Decode the repository name from URL
  const decodedRepoName = repoName ? decodeURIComponent(repoName) : '';

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!decodedRepoName) {
        setError('Repository name is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('[AssessmentReportPage] Fetching assessment for:', decodedRepoName);

        // Public endpoint - no auth required
        const response = await fetch(
          `${API_BASE}/api/github-app/assessments?repository=${encodeURIComponent(decodedRepoName)}`
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch assessment');
        }

        if (data.success && data.data?.Records?.length > 0) {
          setAssessment(data.data.Records[0]);
        } else {
          throw new Error('Assessment not found');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load assessment';
        setError(errorMessage);
        console.error('[AssessmentReportPage] Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [decodedRepoName]);

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderStatusBadge = (hasFeature: boolean, label: string) => (
    <div className="flex items-center gap-2">
      {hasFeature ? (
        <HiCheckCircle className="w-5 h-5 text-green-500" />
      ) : (
        <HiXCircle className="w-5 h-5 text-gray-400" />
      )}
      <span className={hasFeature ? 'text-gray-900' : 'text-gray-500'}>{label}</span>
    </div>
  );

  const calculateHealthScore = (analysis: AssessmentAnalysis): number => {
    let score = 50; // Base score

    // Positive factors
    if (analysis.Has_Tests) score += 15;
    if (analysis.Has_CI) score += 15;
    if (analysis.Has_Docker) score += 5;
    if (analysis.Has_Infrastructure) score += 5;
    if (analysis.Frameworks.length > 0) score += 5;
    if (analysis.Insights.length > analysis.Concerns.length) score += 5;

    // Negative factors
    if (analysis.Concerns.length > 3) score -= 10;
    if (!analysis.Has_Tests && !analysis.Has_CI) score -= 10;

    return Math.max(0, Math.min(100, score));
  };

  // =============================================================================
  // RENDER: LOADING
  // =============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: ERROR
  // =============================================================================

  if (error || !assessment) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-indigo-600 text-white py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-xl font-bold">Equilateral AI</Link>
              <Link to="/login">
                <Button color="light" size="sm">Sign In</Button>
              </Link>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 py-16">
          <Alert color="failure">
            <div className="flex flex-col items-center text-center py-8">
              <HiExclamation className="w-16 h-16 text-red-500 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Assessment Not Found</h2>
              <p className="text-gray-600 mb-6">
                {error || `We couldn't find an assessment for "${decodedRepoName}".`}
              </p>
              <div className="flex gap-4">
                <a href={`https://github.com/${decodedRepoName}`} target="_blank" rel="noopener noreferrer">
                  <Button color="gray">
                    <FaGithub className="w-4 h-4 mr-2" />
                    View on GitHub
                  </Button>
                </a>
                <Link to="/">
                  <Button color="blue">Learn About Equilateral AI</Button>
                </Link>
              </div>
            </div>
          </Alert>
        </div>
      </div>
    );
  }

  const { Analysis: analysis } = assessment;
  const healthScore = calculateHealthScore(analysis);
  const primaryLanguage = Object.keys(analysis.Languages)[0] || 'Unknown';

  // =============================================================================
  // RENDER: MAIN CONTENT
  // =============================================================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-indigo-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-xl font-bold">Equilateral AI</Link>
            <Link to="/login">
              <Button color="light" size="sm">Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-indigo-600 to-indigo-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge color="indigo" size="sm">Initial Assessment</Badge>
            <Badge color="gray" size="sm">{primaryLanguage}</Badge>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{assessment.Repository}</h1>
          <p className="text-indigo-200 mb-6">
            Scanned on {new Date(assessment.Created_At).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href={`https://github.com/${assessment.Repository}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
            >
              <FaGithub className="w-5 h-5" />
              View Repository
              <HiExternalLink className="w-4 h-4" />
            </a>
            {assessment.GitHub_Issue && (
              <a
                href={assessment.GitHub_Issue.URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
              >
                View Issue #{assessment.GitHub_Issue.Number}
                <HiExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Analysis */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Card */}
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <HiDocument className="w-8 h-8 mx-auto text-indigo-600 mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{analysis.File_Count.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">Files</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <HiCode className="w-8 h-8 mx-auto text-indigo-600 mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{analysis.Frameworks.length}</div>
                  <div className="text-sm text-gray-500">Frameworks</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <HiLightBulb className="w-8 h-8 mx-auto text-green-600 mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{analysis.Insights.length}</div>
                  <div className="text-sm text-gray-500">Strengths</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <HiExclamation className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{analysis.Concerns.length}</div>
                  <div className="text-sm text-gray-500">Opportunities</div>
                </div>
              </div>
            </Card>

            {/* Tech Stack */}
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Technology Stack</h2>
              <div className="space-y-4">
                {/* Languages */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Languages</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(analysis.Languages).map(([lang, status]) => (
                      <Badge key={lang} color={status === 'primary' ? 'indigo' : 'gray'} size="lg">
                        {lang}
                        {status === 'primary' && <span className="ml-1 text-xs opacity-75">(primary)</span>}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Frameworks */}
                {analysis.Frameworks.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Frameworks & Libraries</h3>
                    <div className="flex flex-wrap gap-2">
                      {analysis.Frameworks.map((framework) => (
                        <Badge key={framework} color="blue" size="lg">{framework}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Development Features */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Development Infrastructure</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {renderStatusBadge(analysis.Has_Tests, 'Test Suite')}
                    {renderStatusBadge(analysis.Has_CI, 'CI/CD Pipeline')}
                    {renderStatusBadge(analysis.Has_Docker, 'Docker')}
                    {renderStatusBadge(analysis.Has_Infrastructure, 'Infrastructure as Code')}
                  </div>
                </div>
              </div>
            </Card>

            {/* Insights & Concerns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Insights */}
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HiCheckCircle className="w-5 h-5 text-green-500" />
                  What's Working Well
                </h2>
                {analysis.Insights.length > 0 ? (
                  <ul className="space-y-3">
                    {analysis.Insights.map((insight, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-green-500 mt-0.5">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No specific insights detected.</p>
                )}
              </Card>

              {/* Concerns */}
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HiLightBulb className="w-5 h-5 text-amber-500" />
                  Opportunities
                </h2>
                {analysis.Concerns.length > 0 ? (
                  <ul className="space-y-3">
                    {analysis.Concerns.map((concern, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-amber-500 mt-0.5">•</span>
                        {concern}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No concerns identified.</p>
                )}
              </Card>
            </div>

            {/* Directory Structure */}
            {analysis.Directory_Structure.length > 0 && (
              <Card>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HiFolder className="w-5 h-5 text-indigo-600" />
                  Project Structure
                </h2>
                <div className="flex flex-wrap gap-2">
                  {analysis.Directory_Structure.map((dir) => (
                    <code key={dir} className="px-3 py-1 bg-gray-100 rounded text-sm text-gray-700">
                      {dir}/
                    </code>
                  ))}
                </div>
              </Card>
            )}

            {/* Key Files */}
            {analysis.Key_Files && analysis.Key_Files.length > 0 && (
              <Card>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Configuration Files</h2>
                <div className="overflow-x-auto">
                  <Table hoverable>
                    <Table.Head>
                      <Table.HeadCell>File</Table.HeadCell>
                      <Table.HeadCell>Category</Table.HeadCell>
                      <Table.HeadCell>Path</Table.HeadCell>
                    </Table.Head>
                    <Table.Body className="divide-y">
                      {analysis.Key_Files.slice(0, 10).map((file, index) => (
                        <Table.Row key={index} className="bg-white">
                          <Table.Cell className="font-medium text-gray-900">{file.file}</Table.Cell>
                          <Table.Cell>
                            <Badge color="gray" size="sm">{file.category}</Badge>
                          </Table.Cell>
                          <Table.Cell className="text-gray-500 text-sm font-mono">{file.path}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Health Score & CTA */}
          <div className="space-y-6">
            {/* Health Score Card */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Repository Health</h2>
              <div className="text-center mb-4">
                <div className="text-5xl font-bold text-indigo-600 mb-2">{healthScore}</div>
                <div className="text-gray-500">out of 100</div>
              </div>
              <Progress
                progress={healthScore}
                color={healthScore >= 70 ? 'green' : healthScore >= 40 ? 'yellow' : 'red'}
                size="lg"
              />
              <p className="mt-4 text-sm text-gray-600 text-center">
                {healthScore >= 70
                  ? 'Great foundation! Your repository follows many best practices.'
                  : healthScore >= 40
                  ? 'Good start! There are some opportunities for improvement.'
                  : 'Consider implementing testing and CI/CD to improve maintainability.'}
              </p>
            </Card>

            {/* Dependencies */}
            {(analysis.Dependency_Count !== undefined || analysis.Dev_Dependency_Count !== undefined) && (
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Dependencies</h2>
                <div className="space-y-3">
                  {analysis.Dependency_Count !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Production</span>
                      <Badge color="blue">{analysis.Dependency_Count}</Badge>
                    </div>
                  )}
                  {analysis.Dev_Dependency_Count !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Development</span>
                      <Badge color="gray">{analysis.Dev_Dependency_Count}</Badge>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
              <h2 className="text-xl font-bold mb-3">Unlock Full Analysis</h2>
              <p className="text-indigo-100 mb-6 text-sm">
                Get detailed insights including:
              </p>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2">
                  <HiCheckCircle className="w-4 h-4 text-indigo-300" />
                  Dependency vulnerability scan
                </li>
                <li className="flex items-center gap-2">
                  <HiCheckCircle className="w-4 h-4 text-indigo-300" />
                  Architecture diagrams
                </li>
                <li className="flex items-center gap-2">
                  <HiCheckCircle className="w-4 h-4 text-indigo-300" />
                  Code quality metrics
                </li>
                <li className="flex items-center gap-2">
                  <HiCheckCircle className="w-4 h-4 text-indigo-300" />
                  AI-generated specifications
                </li>
                <li className="flex items-center gap-2">
                  <HiCheckCircle className="w-4 h-4 text-indigo-300" />
                  Improvement roadmap
                </li>
              </ul>
              <Link to="/onboarding">
                <Button color="light" className="w-full">
                  Get Started Free
                  <HiArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Card>

            {/* Back to Issue */}
            {assessment.GitHub_Issue && (
              <Card>
                <a
                  href={assessment.GitHub_Issue.URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <FaGithub className="w-5 h-5" />
                  Back to GitHub Issue #{assessment.GitHub_Issue.Number}
                  <HiExternalLink className="w-4 h-4" />
                </a>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <div className="text-xl font-bold mb-2">Equilateral AI</div>
              <p className="text-gray-400 text-sm">AI-powered software governance</p>
            </div>
            <div className="flex gap-6">
              <a href="https://equilateral.ai" className="text-gray-400 hover:text-white transition-colors">
                About
              </a>
              <a href="mailto:support@equilateral.ai" className="text-gray-400 hover:text-white transition-colors">
                Support
              </a>
              <a href="https://github.com/equilateral-ai" className="text-gray-400 hover:text-white transition-colors">
                GitHub
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Pareidolia LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AssessmentReportPage;
